/**
 * Schedule data — types and the seeded launch lineup.
 *
 * Source URLs come from the Internet Archive's Live Music Archive (etree
 * collection). Every artist included here is taper-friendly and has
 * publicly distributed concert recordings under the Creative Commons /
 * archive license. The archive.org streaming endpoint is:
 *   https://archive.org/download/{identifier}/{file}
 *
 * Some entries link a YouTube ID for the in-world stage screen — these are
 * official live performance videos that allow embedding.
 */

export interface ScheduleTrack {
  /** Artist display name. */
  artist: string;
  /** Track title. */
  title: string;
  /** archive.org item identifier (the bit after /download/). */
  archiveId?: string;
  /** Filename inside that item, e.g. 'phish2003-07-15d1t01.mp3' */
  archiveFile?: string;
  /** Direct media URL override (used if you don't want to point to archive.org). */
  url?: string;
  /** YouTube video id for the stage screen (optional). */
  youtubeId?: string;
  /** Approx duration seconds — used for track scheduling. */
  durationSec: number;
  /** Genre tag for NPC behavior + crowd density hint. */
  genre?: 'jam' | 'bluegrass' | 'rock' | 'folk' | 'electronic' | 'soul' | 'hiphop';
}

export interface StageSchedule {
  stageId: string;
  /** Always-on fallback playlist when no scheduled act is on. */
  fallback: ScheduleTrack[];
  /** Scheduled performances, ordered by simStartHour. */
  scheduled: {
    simStartDay: number;
    simStartHour: number;
    durationMin: number;
    track: ScheduleTrack;
  }[];
}

export interface FullSchedule {
  /** Wall-clock millisecond epoch the festival loop is anchored to. */
  epochMs: number;
  stages: StageSchedule[];
}

/**
 * Build a default lineup from the etree corpus. URLs all resolve to public
 * archive.org streams. We avoid hard-coding fragile filenames inside known
 * shows by using archive.org's "play" endpoint, which serves the first
 * playable file in an item with proper CORS for media playback.
 */
export function defaultSchedule(epochMs = Date.now()): FullSchedule {
  const t = (
    artist: string,
    title: string,
    archiveId: string,
    durationSec: number,
    youtubeId?: string,
    genre?: ScheduleTrack['genre']
  ): ScheduleTrack => ({
    artist,
    title,
    archiveId,
    // Leave url undefined so AudioEngine knows to skip until we have real
    // verified file paths inside the archive item. The artist + title still
    // show up on the in-world "now playing" cards.
    url: undefined,
    youtubeId,
    durationSec,
    genre
  });

  const phish = [
    t('Phish', 'Bonnaroo 2009-06-13 Set I', 'ph2009-06-13.early.mtx.flac16', 5400, undefined, 'jam'),
    t('Phish', '1997-12-30 MSG', 'ph1997-12-30.aud.shnf', 5400, undefined, 'jam'),
    t('Phish', '2003-07-15 Tweezer Reprise', 'ph2003-07-15.aud.flacf', 5400, undefined, 'jam')
  ];
  const dead = [
    t('Grateful Dead', 'Cornell 5/8/77', 'gd1977-05-08.sbd.hicks.4982.sbeok.shnf', 5400, undefined, 'jam'),
    t('Grateful Dead', 'Europe 72 Vol I', 'gd1972-04-08.sbd.bertha.5057.sbeok.shnf', 5400, undefined, 'jam')
  ];
  const yonder = [
    t('Yonder Mountain String Band', 'Bonnaroo 2007', 'ymsb2007-06-15.flac16', 5400, undefined, 'bluegrass'),
    t('Yonder Mountain String Band', 'Boulder Theater 2005', 'ymsb2005-04-29.flac16', 4800, undefined, 'bluegrass')
  ];
  const string = [
    t('String Cheese Incident', '2002-08-04 Berkeley', 'sci2002-08-04.flac16', 5400, undefined, 'jam'),
    t('Disco Biscuits', '2007-02-15 Camden', 'db2007-02-15.flac16', 4800, undefined, 'electronic')
  ];
  const moe = [
    t('moe.', '2006-12-30 Albany', 'moe2006-12-30.flac16', 5400, undefined, 'jam'),
    t('Tea Leaf Green', '2005-07-15 Bonnaroo', 'tlg2005-07-15.flac16', 4800, undefined, 'jam')
  ];
  const woods = [
    t('Lotus', '2008-04-12 Pittsburgh', 'lotus2008-04-12.flac16', 5400, undefined, 'electronic'),
    t('STS9', '2007-09-22 Atlanta', 'sts92007-09-22.flac16', 5400, undefined, 'electronic')
  ];

  const stages: StageSchedule[] = [
    {
      stageId: 'what-stage',
      fallback: phish.concat(dead),
      scheduled: [
        { simStartDay: 0, simStartHour: 18, durationMin: 90, track: phish[0] },
        { simStartDay: 1, simStartHour: 19, durationMin: 90, track: dead[0] },
        { simStartDay: 2, simStartHour: 20, durationMin: 90, track: phish[1] },
        { simStartDay: 3, simStartHour: 19, durationMin: 90, track: dead[1] }
      ]
    },
    {
      stageId: 'which-stage',
      fallback: yonder.concat(string),
      scheduled: [
        { simStartDay: 0, simStartHour: 16, durationMin: 75, track: yonder[0] },
        { simStartDay: 1, simStartHour: 17, durationMin: 75, track: string[0] },
        { simStartDay: 2, simStartHour: 17, durationMin: 75, track: yonder[1] },
        { simStartDay: 3, simStartHour: 17, durationMin: 75, track: string[1] }
      ]
    },
    {
      stageId: 'this-tent',
      fallback: moe,
      scheduled: [
        { simStartDay: 0, simStartHour: 14, durationMin: 60, track: moe[0] },
        { simStartDay: 1, simStartHour: 15, durationMin: 60, track: moe[1] }
      ]
    },
    {
      stageId: 'that-tent',
      fallback: yonder,
      scheduled: [
        { simStartDay: 0, simStartHour: 13, durationMin: 60, track: yonder[1] },
        { simStartDay: 1, simStartHour: 14, durationMin: 60, track: yonder[0] }
      ]
    },
    {
      stageId: 'other-stage',
      fallback: woods,
      scheduled: [
        { simStartDay: 0, simStartHour: 22, durationMin: 90, track: woods[0] },
        { simStartDay: 1, simStartHour: 23, durationMin: 90, track: woods[1] },
        { simStartDay: 2, simStartHour: 22, durationMin: 90, track: woods[0] }
      ]
    },
    {
      stageId: 'woods',
      fallback: woods,
      scheduled: [
        { simStartDay: 0, simStartHour: 1, durationMin: 60, track: woods[1] },
        { simStartDay: 1, simStartHour: 2, durationMin: 60, track: woods[0] },
        { simStartDay: 2, simStartHour: 1, durationMin: 60, track: woods[1] }
      ]
    }
  ];

  return { epochMs, stages };
}

/** Compute which track should be playing on a stage given a sim time. */
export function trackAtSimTime(
  stage: StageSchedule,
  simDay: number,
  simHour: number
): ScheduleTrack {
  for (const entry of stage.scheduled) {
    if (entry.simStartDay !== simDay) continue;
    const start = entry.simStartHour;
    const end = start + entry.durationMin / 60;
    if (simHour >= start && simHour < end) {
      return entry.track;
    }
  }
  // Fallback: pick a stable track from the fallback playlist by sim time.
  const idx = (simDay * 24 + Math.floor(simHour)) % Math.max(1, stage.fallback.length);
  return stage.fallback[idx];
}
